import { useState, useEffect, useCallback } from "react";
import { Brain, Loader2, Trash2, Sparkles, Zap, Globe2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import api from "../../lib/api";

export default function BankRulesDrawer({ restaurantId, categories, catLabels, open, onClose, onChange }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!restaurantId || !open) return;
    setLoading(true);
    try {
      const r = await api.listBankRules(restaurantId);
      setRules(r || []);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, [restaurantId, open]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (rule, field, value) => {
    setBusy(rule.rule_id);
    try {
      const updated = await api.updateBankRule(restaurantId, rule.rule_id, { [field]: value });
      setRules(rs => rs.map(r => r.rule_id === rule.rule_id ? updated : r));
      onChange?.();
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setBusy(null); }
  };

  const remove = async (rule) => {
    setBusy(rule.rule_id);
    try {
      await api.deleteBankRule(restaurantId, rule.rule_id);
      toast.success("Regla eliminada");
      setRules(rs => rs.filter(r => r.rule_id !== rule.rule_id));
      onChange?.();
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setBusy(null); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="bank-rules-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-violet-600" />
            Reglas aprendidas
          </SheetTitle>
          <SheetDescription>
            Cada vez que emparejas o creas un gasto desde una transacción bancaria, el sistema aprende el patrón. A partir del 2º uso, la próxima importación crea el gasto automáticamente.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : rules.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
                <Sparkles className="h-6 w-6 opacity-40" />
                Aún no hay reglas. Concilia manualmente algunas transacciones y el sistema las aprenderá solo.
              </CardContent>
            </Card>
          ) : (
            rules.map(rule => (
              <Card
                key={rule.rule_id}
                className={rule.auto_create ? "border-emerald-300 bg-emerald-50/40" : ""}
                data-testid={`bank-rule-${rule.rule_id}`}
              >
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {rule.supplier_name || rule.pattern}
                        </span>
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200">
                          × {rule.count}
                        </span>
                        {rule.auto_create && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-0.5">
                            <Zap className="h-3 w-3" /> Auto
                          </span>
                        )}
                        {rule.share_across_restaurants && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200 inline-flex items-center gap-0.5">
                            <Globe2 className="h-3 w-3" /> Global
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                        {rule.pattern}
                      </div>
                      {rule.last_description && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                          Último: {rule.last_description}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => remove(rule)}
                      disabled={busy === rule.rule_id}
                      className="text-muted-foreground hover:text-red-700 p-1 shrink-0"
                      aria-label="Eliminar regla"
                      data-testid={`bank-rule-delete-${rule.rule_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Select
                        value={rule.category}
                        onValueChange={(v) => toggle(rule, "category", v)}
                        disabled={busy === rule.rule_id}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`bank-rule-cat-${rule.rule_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(categories || []).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-1.5 text-xs shrink-0 cursor-pointer">
                      <Switch
                        checked={!!rule.auto_create}
                        onCheckedChange={(v) => toggle(rule, "auto_create", v)}
                        disabled={busy === rule.rule_id}
                        data-testid={`bank-rule-auto-${rule.rule_id}`}
                      />
                      Auto-crear
                    </label>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
                    <Switch
                      checked={!!rule.share_across_restaurants}
                      onCheckedChange={(v) => toggle(rule, "share_across_restaurants", v)}
                      disabled={busy === rule.rule_id}
                      data-testid={`bank-rule-share-${rule.rule_id}`}
                    />
                    <span className="flex items-center gap-1 text-foreground">
                      <Globe2 className="h-3 w-3" /> Aplicar en todos mis restaurantes
                    </span>
                  </label>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
